import PropTypes from "prop-types";
import React, { Component } from "react";
import * as R from "ramda";
import {
  load,
  submit,
  selectFrontBodyPart,
  selectBackBodyPart,
  deselectFrontBodyPart,
  deselectBackBodyPart,
} from "actions/hraInputQuestionnaireActions";
import LifestyleAgeingNav from "../LifestyleAgeingNav";
import { connect } from "react-redux";
import { SubmissionError } from "redux-form";
import lodash from "lodash";
import DynamicPage from "./dynamic-page";
import { Loader } from "cxa-components";
import { arrayToObject } from "cxa-utils";
import { injectIntl, intlShape } from "react-intl";
import messages from "components/messagesDescription";
import PageWithFormValues from "./PageWithFormValues";

const SelectModule = ({
  questionsInfo,
  sectionNumber,
  nextPage,
  previousPage,
  goTo,
  initialValues,
  ...props
}) => {
  const sectionTag = questionsInfo.sections[sectionNumber].sectionTag;
  switch (sectionTag) {
    case "smoking":
      return (
        <PageWithFormValues
          form={sectionTag}
          nextPage={nextPage}
          previousPage={previousPage}
          goTo={goTo}
          initialValues={initialValues}
          questionTag="smoking_profile"
          questionsInfo={questionsInfo}
          {...props}
        />
      );
    case "alcohol":
      return (
        <PageWithFormValues
          form={sectionTag}
          nextPage={nextPage}
          previousPage={previousPage}
          goTo={goTo}
          initialValues={initialValues}
          questionTag="alcohol_profile"
          questionsInfo={questionsInfo}
          {...props}
        />
      );
    default:
      return (
        <DynamicPage
          form={sectionTag}
          nextPage={nextPage}
          previousPage={previousPage}
          goTo={goTo}
          initialValues={initialValues}
          questionsInfo={questionsInfo}
          {...props}
        />
      );
  }
};
SelectModule.propTypes = {
  questionsInfo: PropTypes.object,
  sectionNumber: PropTypes.number,
  nextPage: PropTypes.func,
  previousPage: PropTypes.func,
  goTo: PropTypes.func,
  initialValues: PropTypes.object,
  selectedLanguage: PropTypes.object,
};

class Questionnaire extends Component {
  constructor(props) {
    super(props);
    this.nextPage = this.nextPage.bind(this);
    this.previousPage = this.previousPage.bind(this);
    this.onClickFrontBodyParts = this.onClickFrontBodyParts.bind(this);
    this.onClickBackBodyParts = this.onClickBackBodyParts.bind(this);
    this.goTo = this.goTo.bind(this);
  }

  componentDidMount() {
    this.props.load();
  }

  goTo(pageNumber) {
    this.props.push(`/wellness/lifestyleAgeing/questionnaire/${pageNumber}`);
    event.preventDefault();
  }

  isKeyHour(v, k) {
    return k.startsWith("question_physical_activity_");
  }

  isKeyMinute(v, k) {
    return k.endsWith("_minutes");
  }

  stopSubmitAndShowFieldErrorTips(fieldNames, errorMessage) {
    const errfields = R.zipObj(fieldNames, R.repeat(errorMessage, fieldNames.length));
    throw new SubmissionError(errfields);
  }

  sanitizePhysicalActivityInputs(values) {
    const intOrZero = value => parseInt(value) || 0;
    const hourToMin = value => value * 60;

    const numSanitized = R.map(intOrZero, values);
    return R.merge(
      R.map(hourToMin, R.pickBy(this.isKeyHour, numSanitized)),
      R.pickBy(this.isKeyMinute, numSanitized)
    );
  }

  // NOTE: Keeping this code while verifying the business requirement
  // assertPhysicalActivity_Required1Hour(sanitized, errMsgRequired) {
  //   const islessThan60 = value => value < 60;

  //   const fail1 = R.keys(
  //     R.filter(islessThan60, R.pickBy(this.isKeyHour, sanitized))
  //   );

  //   if (fail1.length > 0) {
  //     this.setState({
  //       sectionValidationError: { messageID: 'activityRequire1Hour' }
  //     });
  //     this.stopSubmitAndShowFieldErrorTips(fail1, errMsgRequired);
  //     return true;
  //   }
  //   return false;
  // }

  assertPhysicalActivityMax24Hours(sanitized) {
    const { intl } = this.props;
    const fail2 = R.sum(R.values(sanitized));
    const MAX_24H = 24 * 60;
    if (fail2 > MAX_24H) {
      throw new SubmissionError({
        _error: intl.formatMessage(messages.totalActivityExceed24hrs),
      });
    }
  }

  nextPage(isLastPage, sectionTag, values) {
    const newValues = Object.assign({}, values);
    const getModifiedValue = value => {
      let modifiedValue = "";
      Object.keys(value).forEach(key => {
        if (value[key]) {
          modifiedValue += `,${key}`;
        }
      });
      return modifiedValue.substr(1);
    };

    const errMsgRequired = this.props.intl.formatMessage(messages.requiredDescription);

    switch (sectionTag) {
      case "conditions":
        newValues["question_current_health_diagnose"] = getModifiedValue(
          values["question_current_health_diagnose"]
        );
        newValues["question_immediate_family_health"] = getModifiedValue(
          values["question_immediate_family_health"]
        );
        break;
      case "musculoskeletal":
        if (this.props.frontBodyParts.length > 0 || this.props.backBodyParts.length > 0) {
          if (!values["question_musculoskeletal"]) {
            this.stopSubmitAndShowFieldErrorTips(["question_musculoskeletal"], errMsgRequired);
          }
        }

        newValues["question_musculoskeletal_parts"] = JSON.stringify({
          front: this.props.frontBodyParts,
          back: this.props.backBodyParts,
        });
        break;
      case "physical_activity_text_input":
        const sanitized = this.sanitizePhysicalActivityInputs(values);
        // if (this.assertPhysicalActivity_Required1Hour(sanitized, errMsgRequired)) {
        //   return;
        // }
        this.assertPhysicalActivityMax24Hours(sanitized);
        break;
    }

    if (!isLastPage) {
      return this.props.submit(false, sectionTag, newValues).then(() => {
        this.props.push(`/wellness/lifestyleAgeing/questionnaire/${this.props.page + 1}`);
      });
    }
    return this.props
      .submit(true, sectionTag, newValues)
      .then(
        result =>
          result == "ok"
            ? this.props.push("/wellness/lifestyleAgeing/results")
            : alert("Something has gone wrong!")
      );
  }

  previousPage() {
    if (this.props.page > 1) {
      this.props.load().then(() => {
        this.props.push(`/wellness/lifestyleAgeing/questionnaire/${this.props.page - 1}`);
      });
    }
  }

  onClickFrontBodyParts(e) {
    if (lodash.includes(this.props.frontBodyParts, e.target.id)) {
      this.props.deselectFrontBodyPart(e.target.id);
    } else {
      this.props.selectFrontBodyPart(e.target.id);
    }
  }

  onClickBackBodyParts(e) {
    if (lodash.includes(this.props.backBodyParts, e.target.id)) {
      this.props.deselectBackBodyPart(e.target.id);
    } else {
      this.props.selectBackBodyPart(e.target.id);
    }
  }

  getRangeInitialValue(options) {
    if (!Array.isArray(options)) {
      throw new Error(
        `Parameter questionOptions should be an array but is actually ${typeof options}`
      );
    }
    const min = 1;
    const max = options.length;
    const defaultIndex = (max - min) / 2 + min;

    return options[defaultIndex - 1].questionOptionId;
  }

  render() {
    const { page, questionsInfo, fetching, fetched } = this.props;
    const sectionNumber = page - 1;
    const initialValues = section => {
      const initialValues = {};
      section.subsections.forEach(sub => {
        sub.questions.forEach(q => {
          if (!q) {
            return;
          }
          switch (q.displayType) {
            case "text":
              initialValues[`question_${q.tag}`] = q.response ? q.response.userValue : "";
              if (q.units) {
                initialValues[`${q.tag}_unit`] =
                  q.response && q.response.userUnit ? q.response.userUnit : q.units[0].unitValue;
              }
              break;
            case "radio":
              initialValues[`question_${q.tag}`] = q.response ? q.response.userValue : "";
              break;
            case "slider":
              initialValues[`question_${q.tag}`] = q.response ? q.response.userValue : "";
              break;
            case "checkbox":
              initialValues[`question_${q.tag}`] = q.response
                ? arrayToObject(q.response.userValue.split(","))
                : "";
              break;
            case "progress":
              initialValues[`question_${q.tag}`] = q.response
                ? q.response.userValue
                : this.getRangeInitialValue(q.questionOptions);
              break;
            case "hour_minute":
              initialValues[`question_${q.tag}`] = q.response
                ? q.response.userValue.split(".")[0]
                : "";
              initialValues[`${q.tag}_minutes`] = q.response
                ? q.response.userValue.split(".")[1]
                : "";
              break;
            default:
          }
        });
      });
      return initialValues;
    };

    if (fetching || !fetched) {
      return <Loader />;
    }

    return (
      <div className="questionnaire">
        <LifestyleAgeingNav />
        <div>
          <SelectModule
            form={questionsInfo.sections[sectionNumber].sectionTag}
            nextPage={this.nextPage}
            previousPage={this.previousPage}
            goTo={this.goTo}
            initialValues={initialValues(questionsInfo.sections[sectionNumber])}
            sectionNumber={sectionNumber}
            onClickFrontBodyParts={this.onClickFrontBodyParts}
            onClickBackBodyParts={this.onClickBackBodyParts}
            {...this.props}
          />
        </div>
      </div>
    );
  }
}

Questionnaire.propTypes = {
  load: PropTypes.func,
  submit: PropTypes.func,
  selectFrontBodyPart: PropTypes.func,
  deselectFrontBodyPart: PropTypes.func,
  selectBackBodyPart: PropTypes.func,
  deselectBackBodyPart: PropTypes.func,
  frontBodyParts: PropTypes.array,
  backBodyParts: PropTypes.array,
  page: PropTypes.number,
  push: PropTypes.func,
  questionsInfo: PropTypes.object,
  fetching: PropTypes.bool,
  fetched: PropTypes.bool,
  selectedLanguage: PropTypes.object,
  intl: intlShape.isRequired,
};

const mapStateToProps = (
  { hraInputQuestionnaire, localization },
  {
    match: {
      params: { page },
    },
  }
) => ({
  ...hraInputQuestionnaire,
  page: parseInt(page || 1),
  selectedLanguage: localization.selectedLanguage,
});

const mapDispatchToProps = {
  load,
  submit,
  selectFrontBodyPart,
  deselectFrontBodyPart,
  selectBackBodyPart,
  deselectBackBodyPart,
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Questionnaire)
);



// WEBPACK FOOTER //
// ./components/wellness/lifestyleAgeing/questionnaire/answerSomeQuestions.jsx